<template>
  <CCanvasModal hideFooter show verticalAlignment="top" :z-index="zIndex" @update:show="emit('toggleSearch', $event)">
    <div
      :class="['bg-white rounded-lg flex flex-col px-2', { 'admin-search': isAdmin }]"
      style="width: 685px; max-height: 725px; overflow: hidden"
    >
      <!-- Search Input -->
      <div class="px-0 pt-2 pb-0 border-b border-gray-200">
        <div class="relative">
          <div class="absolute left-2.5 top-1/2 transform -translate-y-1/2 z-10">
            <i class="c-icon far fa-search text-gray-400 text-l"></i>
          </div>
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            autofocus
            :class="[
              'w-full pl-8 pr-10 py-2 rounded-sm focus:outline-none',
              isInputFocused ? 'border-gray-500' : 'border-gray-300',
            ]"
            maxlength="100"
            :placeholder="t('canvasUI.CAlgoliaSearch.placeholder')"
            style="border-width: 1px; border-style: solid; box-sizing: border-box"
            @blur="isInputFocused = false"
            @focus="isInputFocused = true"
            @input="handleSearchInput(searchQuery)"
            @keydown="handleSuggestionKeydown"
          />
          <button
            v-if="searchQuery.trim()"
            :class="[
              'absolute right-1 top-1/2 transform -translate-y-1/2 z-10 cursor-pointer transition-colors border-0 bg-transparent',
              isInputFocused ? 'text-gray-600 hover:text-gray-800' : 'text-gray-400 hover:text-gray-600',
            ]"
            @click="clearSearch"
          >
            <CIcon icon="xmark" size="18" />
          </button>

          <!-- Query Suggestions -->
          <div
            v-if="querySuggestions.length > 0 && searchQuery.trim().length >= 2"
            ref="suggestionsDropdownRef"
            class="absolute left-0 right-0 top-full mt-1 bg-white rounded border border-gray-200 shadow-lg z-20 max-h-48 overflow-y-auto"
          >
            <div
              v-for="(suggestion, index) in querySuggestions"
              :key="index"
              :class="[
                'px-4 py-2 cursor-pointer text-sm text-gray-700 border-b border-gray-100 last:border-b-0',
                selectedSuggestionIndex === index ? 'bg-primary6' : 'hover:bg-primary5',
              ]"
              @click="handleSuggestionClick(suggestion)"
            >
              <CIcon class="mr-2 inline" icon="search" size="14" />
              {{ suggestion }}
            </div>
          </div>
        </div>

        <!-- Character counter - shown when approaching limit -->
        <div
          v-if="searchQuery.length > 80"
          class="text-xs text-right px-2"
          :class="searchQuery.length >= 100 ? 'text-red-500' : 'text-gray-500'"
        >
          {{ searchQuery.length }}/100
        </div>

        <div v-if="!showRecentView" class="flex items-center mt-4">
          <!-- Left Tabs -->
          <div class="flex space-x-0 items-center">
            <button
              :class="[
                'h-28px text-14px font-500 transition-colors duration-200 flex items-center shadow-none outline-none focus:outline-none border-0',
                searchType === null
                  ? 'bg-primary5 text-primary px-12px rounded-4px'
                  : 'bg-primary6 text-primary hover:bg-primary5 px-16px',
              ]"
              @click="handleEverywhereClick"
            >
              <span class="uppercase">{{ t('canvasUI.browserApp.content.globalSearch') }}</span>
            </button>
            <button
              v-if="searchType !== 'canvases'"
              :class="[
                'h-28px text-14px font-500 transition-colors duration-200 flex items-center shadow-none outline-none focus:outline-none border-0',
                searchType === 'content'
                  ? 'bg-primary5 text-primary px-16px rounded-4px'
                  : 'bg-primary6 text-primary hover:bg-primary5 px-16px',
              ]"
              @click="searchType === 'content' ? handleEverywhereClick() : (searchType = 'content')"
            >
              <span>{{ t('canvasUI.CAlgoliaSearch.tabs.content') }}</span>
            </button>
            <div v-if="searchType !== 'content' && searchType !== 'canvases'" class="h-16px w-1px bg-primary4"></div>
            <button
              v-if="searchType !== 'content'"
              :class="[
                'h-28px text-14px font-500 transition-colors duration-200 flex items-center shadow-none outline-none focus:outline-none border-0',
                searchType === 'canvases'
                  ? 'bg-primary5 text-primary px-16px rounded-4px'
                  : 'bg-primary6 text-primary hover:bg-primary5 px-16px',
              ]"
              @click="searchType === 'canvases' ? handleEverywhereClick() : (searchType = 'canvases')"
            >
              <span>{{ t('canvasUI.CAlgoliaSearch.tabs.pitchDecks') }}</span>
            </button>
          </div>

          <!-- Filters -->
          <div
            v-if="
              searchType &&
              ((searchType === 'content' && hasContentFilters) || (searchType === 'canvases' && hasCanvasFilters))
            "
            class="flex items-center space-x-1 ml-2"
          >
            <!-- Content Filters -->
            <template v-if="searchType === 'content'">
              <NPopover
                class="c-select-filter"
                placement="bottom-start"
                raw
                :show="showFileTypeDropdown"
                :show-arrow="false"
                trigger="manual"
                @clickoutside="showFileTypeDropdown = false"
              >
                <template #trigger>
                  <NTag
                    class="select-none cursor-pointer"
                    :style="{
                      '--n-color': selectedFileTypes.length ? 'var(--p-primary5)' : 'var(--p-primary6)',
                      '--n-border': '1px solid var(--p-primary5)',
                      '--n-height': '28px',
                    }"
                    :themeOverrides="{
                      borderRadius: '4px',
                    }"
                    @click="showFileTypeDropdown = !showFileTypeDropdown"
                  >
                    <div class="flex items-center">
                      <span>{{ t('canvasUI.CAlgoliaSearch.filters.type') }}</span>
                      <span v-if="selectedFileTypes.length">: {{ selectedFileTypes.length }}</span>
                    </div>
                  </NTag>
                </template>
                <div class="c-select-filter__popover w-[350px] rounded flex flex-col bg-base">
                  <div class="pa-2 max-h-[300px] overflow-y-auto">
                    <div
                      v-for="option in visibleFileTypeOptions"
                      :key="option.value"
                      class="w-full h-7 flex items-center"
                    >
                      <NCheckbox
                        :checked="selectedFileTypes.includes(option.value)"
                        class="px-2 truncate"
                        @update:checked="(checked) => handleFileTypeToggle(option.value, checked)"
                      >
                        {{ option.label }}
                      </NCheckbox>
                    </div>
                  </div>
                  <div class="pa-2" :style="{ borderTop: `1px solid ${themeVars.primary5}` }">
                    <CButton block class="pa-2" text @click="clearFileTypes">
                      {{ t('canvasUI.CAlgoliaSearch.filters.clear') }}
                    </CButton>
                  </div>
                </div>
              </NPopover>

              <!-- Clear Filters Button -->
              <div v-if="hasActiveFilters" class="flex items-center ml-2">
                <div class="h-6 w-px bg-gray-300 mx-2"></div>
                <span
                  class="text-sm text-gray-600 hover:text-gray-800 font-normal cursor-pointer"
                  @click="clearAllFilters"
                >
                  {{ t('canvasUI.CAlgoliaSearch.filters.clear') }}
                </span>
              </div>
            </template>

            <!-- Canvas Filters -->
            <template v-else-if="searchType === 'canvases'">
              <NPopover
                class="c-select-filter"
                placement="bottom-start"
                raw
                :show="showCanvasTypeDropdown"
                :show-arrow="false"
                trigger="manual"
                @clickoutside="showCanvasTypeDropdown = false"
              >
                <template #trigger>
                  <NTag
                    class="select-none cursor-pointer"
                    :style="{
                      '--n-color': selectedCanvasFilters.length ? 'var(--p-primary5)' : 'var(--p-primary6)',
                      '--n-border': '1px solid var(--p-primary5)',
                      '--n-height': '28px',
                    }"
                    :themeOverrides="{
                      borderRadius: '4px',
                    }"
                    @click="showCanvasTypeDropdown = !showCanvasTypeDropdown"
                  >
                    <div class="flex items-center">
                      <span>{{ t('canvasUI.CAlgoliaSearch.filters.type') }}</span>
                      <span v-if="selectedCanvasFilters.length">: {{ selectedCanvasFilters.length }}</span>
                    </div>
                  </NTag>
                </template>
                <div class="c-select-filter__popover w-[350px] rounded flex flex-col bg-base">
                  <div class="pa-2 max-h-[300px] overflow-y-auto">
                    <div
                      v-for="option in visibleCanvasTypeOptions"
                      :key="option.value"
                      class="w-full h-7 flex items-center"
                    >
                      <NCheckbox
                        :checked="selectedCanvasFilters.includes(option.value)"
                        class="px-2 truncate"
                        @update:checked="(checked) => handleCanvasTypeToggle(option.value, checked)"
                      >
                        {{ option.label }}
                      </NCheckbox>
                    </div>
                  </div>
                  <div class="pa-2" :style="{ borderTop: `1px solid ${themeVars.primary5}` }">
                    <CButton block class="pa-2" text @click="clearCanvasFilters">
                      {{ t('canvasUI.CAlgoliaSearch.filters.clear') }}
                    </CButton>
                  </div>
                </div>
              </NPopover>

              <!-- Clear Filters Button -->
              <div v-if="hasActiveFilters" class="flex items-center ml-2">
                <div class="h-6 w-px bg-gray-300 mx-2"></div>
                <span
                  class="text-sm text-gray-600 hover:text-gray-800 font-normal cursor-pointer"
                  @click="clearAllFilters"
                >
                  {{ t('canvasUI.CAlgoliaSearch.filters.clear') }}
                </span>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Tab Content -->
      <div class="flex flex-col w-full flex-1 min-h-0">
        <!-- Recent View (when no search query) -->
        <div v-if="showRecentView" class="flex-1 overflow-y-auto bg-white w-full pl-1 pr-6 py-0">
          <!-- Recent Searches -->
          <div class="space-y-1 mt-4">
            <div
              v-for="search in recentSearches"
              :key="search"
              class="flex items-center py-2 px-0 pl-1 hover:bg-primary5 cursor-pointer rounded"
              @click="handleRecentSearchClick(search)"
            >
              <CIcon class="mr-2" color="var(--p-text2)" icon="clock-rotate-left" size="16" />
              <span class="text-sm text-gray-700">{{ search }}</span>
            </div>
          </div>

          <!-- Recently Opened Documents -->
          <template v-if="recentlyOpenedDocs.length > 0">
            <hr class="border-0 h-px bg-gray-200 mt-2 mb-3 w-full" />

            <div class="flex items-center mb-0">
              <span class="text-sm font-bold text-gray-700">{{
                t('canvasUI.components.fileViewer.recentlyOpened')
              }}</span>
            </div>
            <div :class="isAdmin ? 'space-y-2' : 'space-y-0'">
              <div
                v-for="doc in recentlyOpenedDocs"
                :key="`${doc.type}-${doc.id}`"
                :class="[
                  'flex items-center px-0 hover:bg-primary5 cursor-pointer border-b border-gray-100',
                  isAdmin ? 'py-3' : 'py-0',
                ]"
                @click="handleRecentDocClick(doc, doc.type)"
              >
                <div v-if="doc.type === 'file'" class="relative mr-4">
                  <CImage
                    class="h-14 border-rounded-1 overflow-hidden flex-0"
                    cover
                    :file-data="{ name: doc.name, content_type: doc.file_category as any }"
                    object-fit="cover"
                    :src="recentThumbnails[doc.id] || ''"
                    width="72"
                  />
                  <div
                    v-if="isLoadingThumbnails && !recentThumbnails[doc.id]"
                    class="absolute inset-0 flex items-center justify-center bg-gray-200 bg-opacity-75 border-rounded-1"
                  >
                    <CIcon class="animate-spin" color="var(--p-primary)" icon="spinner" size="20" />
                  </div>
                </div>
                <div
                  v-else
                  class="w-18 h-14 border-rounded-1 mr-4 flex-shrink-0 bg-gray-200 flex items-center justify-center"
                >
                  <CIcon v-if="doc.type === 'canvas'" color="var(--p-text2)" icon="presentation" size="32" />
                  <CIcon v-else color="var(--p-text2)" icon="folder" size="32" />
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-sm font-bold text-gray-900 truncate mb-1">{{ doc.name }}</h3>
                  <p class="text-xs text-gray-500">
                    {{
                      doc.type === 'canvas'
                        ? formatCanvasType(doc.content_type)
                        : doc.type === 'folder'
                          ? 'Folder'
                          : doc.file_category || 'File'
                    }}
                  </p>
                </div>
              </div>
            </div>
          </template>
        </div>

        <div v-else-if="!searchType" class="flex-1 overflow-y-auto bg-white w-full">
          <!-- Search Results View -->
          <div class="w-full px-0">
            <div v-if="isSearching" class="flex flex-col items-center justify-center text-center p-8 gap-4">
              <CIcon class="animate-spin text-6xl" color="var(--p-primary)" icon="spinner" />
            </div>
            <template v-else>
              <div v-if="filteredContentFiles.length > 0" class="mb-0">
                <div class="flex items-center px-0 pt-2 pb-1">
                  <CIcon class="mr-2" color="var(--p-text2)" icon="folder" size="16" />
                  <span class="text-m font-semibold text-gray-900">{{
                    t('canvasUI.CAlgoliaSearch.sections.content')
                  }}</span>
                  <span class="ml-2 text-sm text-gray-500">({{ filteredContentFiles.length }})</span>
                </div>
                <div>
                  <div
                    v-for="item in filteredContentFiles.slice(0, 5)"
                    :key="item.id"
                    :class="[
                      'flex items-center px-0 cursor-pointer border-b border-gray-100 w-full',
                      isAdmin ? 'py-3' : 'py-0',
                      isResultSelected(item) ? 'bg-primary6' : 'hover:bg-primary5',
                    ]"
                    :data-result-selected="isResultSelected(item)"
                    @click="handleResultClick(item, item.type === 'folder' ? 'folder' : 'file')"
                  >
                    <CImage
                      v-if="item.type === 'file'"
                      class="w-18 h-14 border-rounded-1 overflow-hidden flex-shrink-0 mr-4"
                      cover
                      :file-data="item"
                      object-fit="cover"
                      :src="item.picture_url || ''"
                      width="72"
                    />
                    <div
                      v-else
                      class="w-18 h-14 border-rounded-1 mr-4 flex-shrink-0 bg-gray-200 flex items-center justify-center"
                    >
                      <CIcon color="var(--p-text2)" icon="folder" size="32" />
                    </div>

                    <div class="flex-1 min-w-0">
                      <h3 class="text-sm font-bold text-gray-900 truncate mb-1">{{ item.name }}</h3>
                      <p
                        v-if="item.type === 'file' && getSnippet(item)"
                        class="text-xs text-gray-600 mb-1"
                        v-html="getSnippet(item)"
                      ></p>
                      <p class="text-xs text-gray-500">
                        {{ item.type === 'folder' ? 'Folder' : item.file_category || item.content_type || 'File' }}
                      </p>
                    </div>

                    <div class="text-xs text-gray-400">
                      {{ item.type === 'folder' ? item.parent_folder?.name || '' : item.folder?.name || '' }}
                    </div>
                  </div>
                </div>
                <div v-if="filteredContentFiles.length > 5" class="px-0 py-0">
                  <span
                    class="text-sm text-gray-600 hover:text-gray-800 font-bold flex items-center cursor-pointer"
                    @click="searchType = 'content'"
                  >
                    {{ t('canvasUI.CAlgoliaSearch.actions.viewAll') }}
                    <CIcon class="ml-1" icon="chevron-right" size="12" />
                  </span>
                </div>
              </div>

              <div v-if="filteredCanvasFiles.length > 0">
                <div class="flex items-center px-0 pt-2 pb-1">
                  <CIcon class="mr-2" color="var(--p-text2)" icon="presentation" size="16" />
                  <span class="text-m font-semibold text-gray-900">{{
                    t('canvasUI.CAlgoliaSearch.sections.pitchDecks')
                  }}</span>
                  <span class="ml-2 text-sm text-gray-500">({{ filteredCanvasFiles.length }})</span>
                </div>
                <div>
                  <div
                    v-for="item in filteredCanvasFiles.slice(0, 5)"
                    :key="item.id"
                    :class="[
                      'flex items-center px-0 cursor-pointer border-b border-gray-100 w-full',
                      isAdmin ? 'py-3' : 'py-0',
                      isResultSelected(item) ? 'bg-primary6' : 'hover:bg-primary5',
                    ]"
                    :data-result-selected="isResultSelected(item)"
                    @click="handleResultClick(item, 'canvas')"
                  >
                    <div
                      class="w-18 h-14 border-rounded-1 mr-4 flex-shrink-0 bg-gray-200 flex items-center justify-center"
                    >
                      <CIcon color="var(--p-text2)" icon="presentation" size="32" />
                    </div>

                    <div class="flex-1 min-w-0">
                      <h3 class="text-sm font-bold text-gray-900 truncate mb-1">{{ item.name }}</h3>
                      <p class="text-xs text-gray-500">{{ formatCanvasType(item.content_type) }}</p>
                    </div>

                    <div class="text-xs text-gray-400">{{ item.folder?.name || '' }}</div>
                  </div>
                </div>
                <div v-if="filteredCanvasFiles.length > 5" class="px-0 py-0">
                  <span
                    class="text-sm text-gray-600 hover:text-gray-800 font-bold flex items-center cursor-pointer"
                    @click="searchType = 'canvases'"
                  >
                    {{ t('canvasUI.CAlgoliaSearch.actions.viewAll') }}
                    <CIcon class="ml-1" icon="chevron-right" size="12" />
                  </span>
                </div>
              </div>

              <div
                v-if="searchError || shouldShowNoResults"
                class="flex flex-col items-center justify-center text-center p-8 gap-4"
              >
                <CIcon
                  class="text-6xl"
                  :color="searchError ? 'var(--p-error)' : 'var(--p-text3)'"
                  fa-type="fal"
                  :icon="searchError ? 'exclamation-triangle' : 'radar'"
                />
                <p :class="searchError ? 'text-error font-semibold' : 'text-text2'">
                  {{ searchError || t('canvasUI.CGlobalSearch.noResults') }}
                </p>
              </div>
            </template>
          </div>
        </div>
        <div v-else class="flex-1 overflow-y-auto bg-white w-full">
          <div class="w-full px-0">
            <div v-if="isSearching" class="flex flex-col items-center justify-center text-center p-8 gap-4">
              <CIcon class="animate-spin text-6xl" color="var(--p-primary)" icon="spinner" />
            </div>
            <template v-else>
              <div class="flex items-center px-0 pt-2 pb-1">
                <CIcon v-if="searchType === 'content'" class="mr-2" color="var(--p-text2)" icon="folder" size="16" />
                <CIcon v-else class="mr-2" color="var(--p-text2)" icon="presentation" size="16" />
                <span class="text-m font-semibold text-gray-900">
                  {{
                    searchType === 'content'
                      ? t('canvasUI.CAlgoliaSearch.sections.content')
                      : t('canvasUI.CAlgoliaSearch.sections.pitchDecks')
                  }}
                </span>
                <span class="ml-2 text-sm text-gray-500">
                  ({{ searchType === 'content' ? filteredContentFiles.length : filteredCanvasFiles.length }})
                </span>
              </div>

              <div>
                <div
                  v-for="item in searchType === 'content' ? filteredContentFiles : filteredCanvasFiles"
                  :key="item.id"
                  :class="[
                    'flex items-center px-0 cursor-pointer border-b border-gray-100 w-full',
                    isAdmin ? 'py-3' : 'py-0',
                    isResultSelected(item) ? 'bg-gray-100' : 'hover:bg-gray-50',
                  ]"
                  :data-result-selected="isResultSelected(item)"
                  @click="
                    handleResultClick(
                      item,
                      searchType === 'content' ? (item.type === 'folder' ? 'folder' : 'file') : 'canvas',
                    )
                  "
                >
                  <CImage
                    v-if="searchType === 'content' && item.type === 'file'"
                    class="w-18 h-14 border-rounded-1 overflow-hidden flex-shrink-0 mr-4"
                    cover
                    :file-data="item"
                    object-fit="cover"
                    :src="item.picture_url || ''"
                    width="72"
                  />
                  <div
                    v-else
                    class="w-18 h-14 border-rounded-1 mr-4 flex-shrink-0 bg-gray-200 flex items-center justify-center"
                  >
                    <CIcon v-if="searchType === 'content'" color="var(--p-text2)" icon="folder" size="32" />
                    <CIcon v-else color="var(--p-text2)" icon="presentation" size="32" />
                  </div>

                  <div class="flex-1 min-w-0">
                    <h3 class="text-sm font-bold text-gray-900 truncate mb-1">{{ item.name }}</h3>
                    <p
                      v-if="searchType === 'content' && item.type === 'file' && getSnippet(item)"
                      class="text-xs text-gray-600 mb-1"
                      v-html="getSnippet(item)"
                    ></p>
                    <p class="text-xs text-gray-500">
                      {{
                        searchType === 'content'
                          ? item.type === 'folder'
                            ? 'Folder'
                            : item.file_category || item.content_type || 'File'
                          : formatCanvasType(item.content_type)
                      }}
                    </p>
                  </div>

                  <div class="text-xs text-gray-400">
                    {{
                      searchType === 'content' && item.type === 'folder'
                        ? item.parent_folder?.name || ''
                        : item.folder?.name || ''
                    }}
                  </div>
                </div>
              </div>

              <div
                v-if="searchError || shouldShowNoResults"
                class="flex flex-col items-center justify-center text-center p-8 gap-4"
              >
                <CIcon
                  class="text-6xl"
                  :color="searchError ? 'var(--p-error)' : 'var(--p-text3)'"
                  fa-type="fal"
                  :icon="searchError ? 'exclamation-triangle' : 'radar'"
                />
                <p :class="searchError ? 'text-error font-semibold' : 'text-text2'">
                  {{ searchError || t('canvasUI.CGlobalSearch.noResults') }}
                </p>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Shortcuts Instructions -->
      <div
        v-if="!showRecentView"
        class="flex flex-wrap line-height-6 pt-4 pb-2 px-6 border-t border-gray-200 gap-y-4 gap-x-4"
      >
        <CShortcut>
          <CShortcutIcon icon="arrow-up" />
          {{ t('canvasUI.CGlobalSearch.shortcuts.or') }}
          <CShortcutIcon icon="arrow-down" />
          {{ t('canvasUI.CGlobalSearch.shortcuts.navigate') }}
        </CShortcut>

        <CShortcut>
          <CShortcutIcon icon="arrow-turn-down-left" />
          {{ t('canvasUI.CGlobalSearch.shortcuts.open') }}
        </CShortcut>

        <CShortcut>
          <CShortcutIcon icon="slash-forward" />
          {{ t('canvasUI.CGlobalSearch.shortcuts.find') }}
        </CShortcut>

        <CShortcut>
          <CShortcutIcon :text="t('canvasUI.CGlobalSearch.shortcuts.esc')" />
          {{ t('canvasUI.CGlobalSearch.shortcuts.exit') }}
        </CShortcut>
      </div>
    </div>
  </CCanvasModal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import CCanvasModal from '@lib/components/CCanvasModal/CCanvasModal.vue'
import CShortcut from '@lib/components/CShortcut/CShortcut.vue'
import CShortcutIcon from '@lib/components/CShortcut/CShortcutIcon.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CImage from '@lib/components/CImage/CImage.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { useCoreApi } from '@lib/composables/useCoreApi'
import { NTag, NPopover, NCheckbox, useMessage } from 'naive-ui'
import { track } from '@lib/sdk/api/modules/track'
import { CDP_EVENT_TYPE } from '@lib/constants/cdp.const'
import { sanitizeHtml } from '@lib/util/string'

interface Props {
  zIndex?: number
  instanceId?: string
  isAdmin?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  instanceId: 'default_instance',
})

const emit = defineEmits<{
  toggleSearch: [value: boolean]
  resultClick: [item: any, type: 'canvas' | 'file' | 'folder']
}>()

const { t } = useI18n()
const message = useMessage()
const { coreRestApiAxios } = useCoreApi()
const themeVars = useThemeVars()

const searchType = ref<'content' | 'canvases' | null>(null)
const searchQuery = ref('')
const showRecentView = ref(true)
const suggestionsDropdownRef = ref<HTMLElement | null>(null)
const searchInputRef = ref<HTMLInputElement | null>(null)

// Filter states
const selectedFileTypes = ref<string[]>([])

// Dropdown visibility states
const showFileTypeDropdown = ref(false)

// Canvas filter states
const selectedCanvasFilters = ref<string[]>([])
const showCanvasTypeDropdown = ref(false)

// Filter options
const fileTypeOptions = [
  { label: 'Folder', value: 'folder' },
  { label: 'PDF', value: 'pdf' },
  { label: 'Document', value: 'document' },
  { label: 'Presentation', value: 'presentation' },
  { label: 'Spreadsheet', value: 'spreadsheet' },
  { label: 'Image', value: 'image' },
  { label: 'Video', value: 'video' },
  { label: 'Audio', value: 'audio' },
  { label: 'AR', value: 'ar' },
  { label: 'Web', value: 'web' },
]

// Canvas filter options
const canvasTypeOptions = computed(() => {
  const options = [
    { label: t('canvasUI.CAlgoliaSearch.canvasFilters.templates'), value: 'template' },
    { label: t('canvasUI.CAlgoliaSearch.canvasFilters.products'), value: 'section' },
    { label: t('canvasUI.CAlgoliaSearch.canvasFilters.blocks'), value: 'block' },
  ]

  if (!props.isAdmin) {
    options.unshift({ label: t('canvasUI.CAlgoliaSearch.canvasFilters.saved'), value: 'saved_canvas' })
  }

  if (props.isAdmin) {
    options.splice(1, 0, {
      label: t('canvasUI.CAlgoliaSearch.canvasFilters.productTemplates'),
      value: 'product_template',
    })
  }

  return options
})

// Allowed filter values for validation
const ALLOWED_CANVAS_TYPES = ['saved_canvas', 'template', 'product_template', 'section', 'block']
const ALLOWED_FILE_TYPES = [
  'folder',
  'pdf',
  'image',
  'audio',
  'video',
  'document',
  'presentation',
  'spreadsheet',
  'ar',
  'web',
]

// Filter canvas type options to only show those with results
const visibleCanvasTypeOptions = computed(() => {
  if (showRecentView.value || !searchQuery.value.trim()) {
    return canvasTypeOptions.value
  }

  return canvasTypeOptions.value.filter((option) => availableCanvasTypes.value.has(option.value))
})

// Computed to check if any filters are active
const hasActiveFilters = computed(() => selectedFileTypes.value.length > 0 || selectedCanvasFilters.value.length > 0)

// Clear all filters
const clearAllFilters = () => {
  selectedFileTypes.value = []
  selectedCanvasFilters.value = []
  debouncedFilterSearch()
}

// Handle EVERYWHERE button click
const handleEverywhereClick = async () => {
  selectedFileTypes.value = []
  selectedCanvasFilters.value = []
  searchType.value = null

  // If we have cached unfiltered results, just apply filters
  if (
    searchQuery.value.trim() &&
    (unfilteredContentResults.value.length > 0 || unfilteredCanvasResults.value.length > 0)
  ) {
    applyFiltersToResults()
  } else if (searchQuery.value.trim()) {
    // Otherwise, fetch new results
    try {
      skipNextSuggestionFetch.value = true
      await performSearch(searchQuery.value)
    } catch (error) {
      console.error('[handleEverywhereClick] Search failed:', error)
    } finally {
      skipNextSuggestionFetch.value = false
    }
  }
}

// Recent searches from local storage
const recentSearches = ref<string[]>([])

// Recently opened docs from storage
interface RecentlyOpenedDoc {
  id: string
  name: string
  type: 'file' | 'canvas' | 'folder'
  file_category?: string
  content_type?: string
  openedAt: number
}

const recentlyOpenedDocs = ref<RecentlyOpenedDoc[]>([])
const recentThumbnails = ref<Record<string, string>>({})
const isLoadingThumbnails = ref(false)

const skipNextSuggestionFetch = ref(false)

const isInputFocused = ref(false)

// Load recent searches from local storage
const loadRecentSearches = () => {
  if (!props.instanceId) return

  try {
    const suffix = props.isAdmin ? '-admin' : ''
    const storageKey = `algolia-recent-searches-${props.instanceId}${suffix}`
    const saved = localStorage.getItem(storageKey)
    if (saved) {
      recentSearches.value = JSON.parse(saved)
    }
  } catch (error) {
    console.warn('Failed to load recent searches from localStorage:', error)
    recentSearches.value = []
  }
}

const loadRecentlyOpenedDocs = async () => {
  if (!props.instanceId) return

  try {
    const suffix = props.isAdmin ? '-admin' : ''
    const storageKey = `algolia-recent-docs-${props.instanceId}${suffix}`
    const saved = localStorage.getItem(storageKey)
    if (saved) {
      recentlyOpenedDocs.value = JSON.parse(saved)

      // Fetch thumbnails for file items
      await fetchRecentThumbnails()
    }
  } catch (error) {
    console.warn('Failed to load recently opened documents from localStorage:', error)
    recentlyOpenedDocs.value = []
  }
}

// Fetch thumbnails for recently opened files
const fetchRecentThumbnails = async () => {
  const recentFiles = recentlyOpenedDocs.value.filter((doc) => doc.type === 'file')

  if (recentFiles.length === 0) return

  if (!coreRestApiAxios?.value) return

  isLoadingThumbnails.value = true

  try {
    const fileIds = recentFiles.map((file) => file.id)

    const data = await coreRestApiAxios.value.post<unknown, { thumbnails: Record<string, string> }>(
      'protected/algolia/search',
      {
        thumbnailsOnly: true,
        fileIds,
        instanceId: props.instanceId,
        isAdminApp: props.isAdmin === true,
      },
    )

    recentThumbnails.value = data?.thumbnails || {}
  } catch (error) {
    console.warn('Failed to fetch recent thumbnails:', error)
  } finally {
    isLoadingThumbnails.value = false
  }
}

// Save search term to local storage
const saveSearchTerm = (searchTerm: string) => {
  if (!searchTerm.trim() || !props.instanceId) return

  const term = searchTerm.trim()
  const current = [...recentSearches.value]

  const existingIndex = current.indexOf(term)
  if (existingIndex > -1) {
    current.splice(existingIndex, 1)
  }

  current.unshift(term)

  // Keep only last 5
  const updated = current.slice(0, 5)
  recentSearches.value = updated

  try {
    const suffix = props.isAdmin ? '-admin' : ''
    const storageKey = `algolia-recent-searches-${props.instanceId}${suffix}`
    localStorage.setItem(storageKey, JSON.stringify(updated))
  } catch (error) {
    console.warn('Failed to save recent searches to localStorage:', error)
  }
}

const extractIdFromObjectId = (objectID: string | undefined, pattern: string): string | undefined => {
  if (!objectID || typeof objectID !== 'string') return undefined

  const parts = objectID.split(pattern)
  if (parts.length === 2 && parts[1] && parts[1].trim()) {
    return parts[1].trim()
  }

  return undefined
}

const saveRecentlyOpenedDoc = (item: any, type: 'file' | 'canvas' | 'folder') => {
  if (!props.instanceId) return

  let extractedId: string | undefined

  if (type === 'file') {
    extractedId = item.id || item.file_id || extractIdFromObjectId(item.objectID, '_file_')
  } else if (type === 'canvas') {
    extractedId = item.id || item.canvas_id || extractIdFromObjectId(item.objectID, '_canvas_')
  } else if (type === 'folder') {
    extractedId = item.id || item.folder_id || extractIdFromObjectId(item.objectID, '_folder_')
  }

  if (!extractedId) {
    console.warn('[saveRecentlyOpenedDoc] No valid ID found for item:', item)
    return
  }

  const doc: RecentlyOpenedDoc = {
    id: extractedId,
    name: item.name,
    type,
    file_category: item.file_category,
    content_type: item.content_type,
    openedAt: Date.now(),
  }

  const current = [...recentlyOpenedDocs.value]

  const existingIndex = current.findIndex((d) => d.id === doc.id && d.type === doc.type)
  if (existingIndex > -1) {
    current.splice(existingIndex, 1)
  }

  current.unshift(doc)

  const updated = current.slice(0, 5)
  recentlyOpenedDocs.value = updated

  try {
    const suffix = props.isAdmin ? '-admin' : ''
    const storageKey = `algolia-recent-docs-${props.instanceId}${suffix}`
    localStorage.setItem(storageKey, JSON.stringify(updated))
  } catch (error) {
    console.warn('Failed to save recently opened document to localStorage:', error)
  }
}

// Algolia search results
const contentResults = ref<any[]>([])
const canvasResults = ref<any[]>([])
// Store unfiltered results to compute available filter options
const unfilteredContentResults = ref<any[]>([])
const unfilteredCanvasResults = ref<any[]>([])
const isSearching = ref(false)
const hasSearchCompleted = ref(false)
let currentSearchId = 0
const querySuggestions = ref<string[]>([])
const selectedSuggestionIndex = ref(-1)
const selectedResultIndex = ref(-1)
const searchError = ref<string | null>(null)

const filteredContentFiles = computed(() => {
  if (showRecentView.value || !searchQuery.value.trim()) {
    return []
  }

  return contentResults.value
})

const filteredCanvasFiles = computed(() => {
  if (showRecentView.value || !searchQuery.value.trim()) {
    return []
  }

  return canvasResults.value
})

// Available file types from current search results (unfiltered)
const availableFileTypes = computed(() => {
  if (showRecentView.value || !searchQuery.value.trim()) {
    return new Set<string>()
  }

  const types = new Set<string>()

  unfilteredContentResults.value.forEach((item: any) => {
    if (item.type === 'folder') {
      types.add('folder')
    } else if (item.type === 'file' && item.file_category) {
      types.add(item.file_category)
    }
  })

  return types
})

// Available canvas types from current search results (unfiltered)
const availableCanvasTypes = computed(() => {
  if (showRecentView.value || !searchQuery.value.trim()) {
    return new Set<string>()
  }

  const types = new Set<string>()

  unfilteredCanvasResults.value.forEach((item: any) => {
    if (item.content_type) {
      types.add(item.content_type)
    }
  })

  return types
})

// Filter file type options to only show those with results
const visibleFileTypeOptions = computed(() => {
  if (showRecentView.value || !searchQuery.value.trim()) {
    return fileTypeOptions
  }

  return fileTypeOptions.filter((option) => availableFileTypes.value.has(option.value))
})

// Check if any filters are applicable for content
const hasContentFilters = computed(() => {
  return visibleFileTypeOptions.value.length > 0 || selectedFileTypes.value.length > 0
})

// Check if any filters are applicable for canvases
const hasCanvasFilters = computed(() => {
  return visibleCanvasTypeOptions.value.length > 0 || selectedCanvasFilters.value.length > 0
})

const shouldShowNoResults = computed(() => {
  if (showRecentView.value || !searchQuery.value.trim() || isSearching.value || !hasSearchCompleted.value) return false

  if (searchType.value === 'content') {
    return filteredContentFiles.value.length === 0
  } else if (searchType.value === 'canvases') {
    return filteredCanvasFiles.value.length === 0
  } else {
    return filteredContentFiles.value.length === 0 && filteredCanvasFiles.value.length === 0
  }
})

// Property for keyboard-navigable results
const navigableResults = computed(() => {
  if (showRecentView.value || !searchQuery.value.trim()) return []

  const results: Array<{ item: any; type: 'file' | 'folder' | 'canvas' }> = []

  if (searchType.value === 'content') {
    filteredContentFiles.value.forEach((item) => {
      results.push({ item, type: item.type === 'folder' ? 'folder' : 'file' })
    })
  } else if (searchType.value === 'canvases') {
    filteredCanvasFiles.value.forEach((item) => {
      results.push({ item, type: 'canvas' })
    })
  } else {
    filteredContentFiles.value.slice(0, 5).forEach((item) => {
      results.push({ item, type: item.type === 'folder' ? 'folder' : 'file' })
    })
    filteredCanvasFiles.value.slice(0, 5).forEach((item) => {
      results.push({ item, type: 'canvas' })
    })
  }

  return results
})

// Get unique item identifier
const getItemId = (item: any) => {
  return item.id || item.file_id || item.folder_id || item.canvas_id
}

// Check if a result item is selected via keyboard navigation
const isResultSelected = (item: any) => {
  if (selectedResultIndex.value < 0) return false
  const selected = navigableResults.value[selectedResultIndex.value]
  if (!selected) return false

  const itemType = item.type === 'folder' ? 'folder' : item.type === 'file' ? 'file' : 'canvas'
  return getItemId(selected.item) === getItemId(item) && selected.type === itemType
}

// Reset result selection when search type changes
watch(searchType, () => {
  selectedResultIndex.value = -1
  // Close any open filter dropdowns
  showFileTypeDropdown.value = false
  showCanvasTypeDropdown.value = false
})

// Auto-clear file type filter if it's no longer available in results
watch(visibleFileTypeOptions, (newOptions) => {
  if (selectedFileTypes.value.length > 0) {
    const availableValues = new Set(newOptions.map((option) => option.value))
    const stillAvailable = selectedFileTypes.value.filter((type) => availableValues.has(type))

    if (stillAvailable.length !== selectedFileTypes.value.length) {
      selectedFileTypes.value = stillAvailable
      // Trigger search to refresh results
      if (searchQuery.value.trim()) {
        performSearch(searchQuery.value.trim())
      }
    }
  }
})

// Auto-clear canvas filter if it's no longer available in results
watch(visibleCanvasTypeOptions, (newOptions) => {
  if (selectedCanvasFilters.value.length > 0) {
    const availableValues = new Set(newOptions.map((option) => option.value))
    const stillAvailable = selectedCanvasFilters.value.filter((type) => availableValues.has(type))

    if (stillAvailable.length !== selectedCanvasFilters.value.length) {
      selectedCanvasFilters.value = stillAvailable
      // Trigger search to refresh results
      if (searchQuery.value.trim()) {
        performSearch(searchQuery.value.trim())
      }
    }
  }
})

// Scroll selected result into view
const scrollSelectedResultIntoView = () => {
  nextTick(() => {
    const selectedElement = document.querySelector('[data-result-selected="true"]')
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  })
}

// Debounce timer for saving search terms
let saveSearchTimer: NodeJS.Timeout | null = null

// Fetch query suggestions scoped to user
const fetchQuerySuggestions = async (query: string) => {
  if (!query.trim() || query.trim().length < 2 || !props.instanceId) {
    querySuggestions.value = []
    selectedSuggestionIndex.value = -1
    return
  }

  try {
    const results = await searchViaBackend(query.trim())

    const contentHits = results.content?.hits || []
    const canvasHits = results.canvas?.hits || []

    // Extract unique names
    const suggestions = new Set<string>()

    contentHits.forEach((hit: any) => {
      if (hit.name) suggestions.add(hit.name)
    })

    canvasHits.forEach((hit: any) => {
      if (hit.name) suggestions.add(hit.name)
    })

    querySuggestions.value = Array.from(suggestions).slice(0, 5)
    selectedSuggestionIndex.value = -1
  } catch (error: any) {
    querySuggestions.value = []
    selectedSuggestionIndex.value = -1

    if (error.message === 'AUTH_REQUIRED' || error.message === 'AUTH_EXPIRED' || error.message === 'AUTH_FORBIDDEN') {
      console.warn('[fetchQuerySuggestions] Authentication error:', error.message)
    } else {
      console.warn('[fetchQuerySuggestions] Failed to fetch suggestions:', error.message)
    }
  }
}

// Perform Algolia search
const performSearch = async (query: string) => {
  if (!query.trim()) {
    contentResults.value = []
    canvasResults.value = []
    unfilteredContentResults.value = []
    unfilteredCanvasResults.value = []
    hasSearchCompleted.value = false
    isSearching.value = false
    searchError.value = null
    return
  }

  isSearching.value = true
  hasSearchCompleted.value = false
  searchError.value = null

  // Generate unique ID for this search to prevent race conditions
  const searchId = ++currentSearchId

  // Capture flag value synchronously before any async operations
  const shouldSkipSuggestionFetch = skipNextSuggestionFetch.value
  if (shouldSkipSuggestionFetch) {
    skipNextSuggestionFetch.value = false
  }

  try {
    // Validate canvas type filter
    const validCanvasFilters = selectedCanvasFilters.value.filter((filter) => ALLOWED_CANVAS_TYPES.includes(filter))

    if (selectedCanvasFilters.value.length !== validCanvasFilters.length) {
      const invalid = selectedCanvasFilters.value.filter((filter) => !ALLOWED_CANVAS_TYPES.includes(filter))
      console.warn('[CAlgoliaSearch] Invalid canvas filters removed:', invalid)
      selectedCanvasFilters.value = validCanvasFilters
    }

    // Validate file type filter
    const validFileTypes = selectedFileTypes.value.filter((type) => ALLOWED_FILE_TYPES.includes(type))

    if (selectedFileTypes.value.length !== validFileTypes.length) {
      const invalid = selectedFileTypes.value.filter((type) => !ALLOWED_FILE_TYPES.includes(type))
      console.warn('[CAlgoliaSearch] Invalid file type filters removed:', invalid)
      selectedFileTypes.value = validFileTypes
    }

    // Fetch unfiltered results from backend (no filters sent to Algolia)
    const results = await searchViaBackend(query.trim(), {
      canvas: '',
      content: '',
    })

    // Only update results if the query still matches
    if (query.trim() !== searchQuery.value.trim()) {
      return
    }

    // Store unfiltered results for computing available filter options
    unfilteredContentResults.value = results.content?.hits || []
    unfilteredCanvasResults.value = results.canvas?.hits || []

    // Apply filters client-side
    let filteredContent = unfilteredContentResults.value
    let filteredCanvas = unfilteredCanvasResults.value

    if (validFileTypes.length > 0) {
      filteredContent = filteredContent.filter((item: any) => {
        if (validFileTypes.includes('folder') && item.type === 'folder') {
          return true
        }
        if (item.type === 'file') {
          return validFileTypes.includes(item.file_category)
        }
        return false
      })
    }

    if (validCanvasFilters.length > 0) {
      filteredCanvas = filteredCanvas.filter((item: any) => validCanvasFilters.includes(item.content_type))
    }

    contentResults.value = filteredContent
    canvasResults.value = filteredCanvas
    selectedResultIndex.value = -1
    searchError.value = null

    // Update query suggestions to match the current results
    // Use the captured flag value to avoid race conditions
    if (query.trim().length >= 2 && !shouldSkipSuggestionFetch) {
      fetchQuerySuggestions(query.trim())
    }

    // Track search performed
    const totalResults = contentResults.value.length + canvasResults.value.length
    trackAlgoliaSearchPerformed(query.trim(), totalResults)

    // Track no results if applicable
    if (totalResults === 0) {
      trackAlgoliaSearchNoResults(query.trim())
    }

    await nextTick()
    setTimeout(() => {
      // Only mark as complete if this is still the current search
      if (searchId === currentSearchId) {
        isSearching.value = false
        hasSearchCompleted.value = true
      }
    }, 50)
  } catch (error: any) {
    console.error('Search error:', error)

    // Only update UI state if this is still the current search
    if (searchId === currentSearchId) {
      contentResults.value = []
      canvasResults.value = []
      isSearching.value = false
      hasSearchCompleted.value = true

      if (error.message === 'AUTH_REQUIRED') {
        searchError.value = 'Please sign in to search'
      } else if (error.message === 'AUTH_EXPIRED') {
        searchError.value = 'Your session has expired. Please sign in again'
      } else if (error.message === 'AUTH_FORBIDDEN') {
        searchError.value = 'You do not have permission to search this instance'
      } else {
        searchError.value = 'Search failed. Please try again'
      }
    }
  }
}

let searchTimer: NodeJS.Timeout | null = null
let filterChangeTimer: NodeJS.Timeout | null = null

function validateSearchInput(query: string): string {
  if (!query || typeof query !== 'string') return ''

  return query
}

// Apply filters client-side to cached unfiltered results
function applyFiltersToResults() {
  // Don't apply filters if we don't have unfiltered results yet
  if (unfilteredContentResults.value.length === 0 && unfilteredCanvasResults.value.length === 0) {
    return
  }

  // Validate filters
  const validFileTypes = selectedFileTypes.value.filter((type) => ALLOWED_FILE_TYPES.includes(type))
  const validCanvasFilters = selectedCanvasFilters.value.filter((type) => ALLOWED_CANVAS_TYPES.includes(type))

  // Apply filters client-side
  let filteredContent = unfilteredContentResults.value
  let filteredCanvas = unfilteredCanvasResults.value

  if (validFileTypes.length > 0) {
    filteredContent = filteredContent.filter((item: any) => {
      if (validFileTypes.includes('folder') && item.type === 'folder') {
        return true
      }
      if (item.type === 'file') {
        return validFileTypes.includes(item.file_category)
      }
      return false
    })
  }

  if (validCanvasFilters.length > 0) {
    filteredCanvas = filteredCanvas.filter((item: any) => validCanvasFilters.includes(item.content_type))
  }

  contentResults.value = filteredContent
  canvasResults.value = filteredCanvas
  selectedResultIndex.value = -1
}

const debouncedFilterSearch = () => {
  if (filterChangeTimer) clearTimeout(filterChangeTimer)

  if (searchTimer) clearTimeout(searchTimer)

  filterChangeTimer = setTimeout(async () => {
    if (searchQuery.value.trim() && !showRecentView.value) {
      // Apply filters to cached results instead of making a new backend call
      applyFiltersToResults()
    }
  }, 500)
}

// Handle search input changes dynamically
const handleSearchInput = (query: string) => {
  const validatedQuery = validateSearchInput(query)
  searchQuery.value = validatedQuery

  if (validatedQuery.trim()) {
    showRecentView.value = false
    hasSearchCompleted.value = false

    // Clear suggestions if query is too short
    if (validatedQuery.trim().length < 2) {
      querySuggestions.value = []
      selectedSuggestionIndex.value = -1
    }

    if (searchTimer) clearTimeout(searchTimer)
    searchTimer = setTimeout(() => {
      performSearch(validatedQuery)
    }, 500)

    if (saveSearchTimer) clearTimeout(saveSearchTimer)
    saveSearchTimer = setTimeout(() => {
      saveSearchTerm(validatedQuery)
    }, 1000) // Save after 1 second of no typing
  } else {
    showRecentView.value = true
    contentResults.value = []
    canvasResults.value = []
    unfilteredContentResults.value = []
    unfilteredCanvasResults.value = []
    querySuggestions.value = []
    selectedSuggestionIndex.value = -1
    selectedResultIndex.value = -1
    hasSearchCompleted.value = false

    if (searchTimer) clearTimeout(searchTimer)
    if (saveSearchTimer) clearTimeout(saveSearchTimer)
  }
}

// Clear search input
const clearSearch = () => {
  searchQuery.value = ''
  showRecentView.value = true
  contentResults.value = []
  canvasResults.value = []
  unfilteredContentResults.value = []
  unfilteredCanvasResults.value = []
  querySuggestions.value = []
  selectedSuggestionIndex.value = -1
  selectedResultIndex.value = -1
  hasSearchCompleted.value = false
  isSearching.value = false
  searchError.value = null

  if (searchTimer) clearTimeout(searchTimer)
  if (saveSearchTimer) clearTimeout(saveSearchTimer)
}

// Handle recent search click
const handleRecentSearchClick = async (searchTerm: string) => {
  const validatedSearchTerm = validateSearchInput(searchTerm)

  if (!validatedSearchTerm.trim()) {
    console.warn('[handleRecentSearchClick] Invalid search term provided')
    return
  }

  try {
    skipNextSuggestionFetch.value = true
    searchQuery.value = validatedSearchTerm
    showRecentView.value = false
    querySuggestions.value = []
    saveSearchTerm(validatedSearchTerm)
    await performSearch(validatedSearchTerm)
  } catch (error) {
    console.error('[handleRecentSearchClick] Search failed:', error)
  } finally {
    skipNextSuggestionFetch.value = false
  }
}

// Handle suggestion click
const handleSuggestionClick = async (suggestion: string) => {
  const validatedSuggestion = validateSearchInput(suggestion)

  if (!validatedSuggestion.trim()) {
    console.warn('[handleSuggestionClick] Invalid suggestion provided')
    return
  }

  try {
    querySuggestions.value = []
    selectedSuggestionIndex.value = -1

    skipNextSuggestionFetch.value = true

    searchQuery.value = validatedSuggestion
    saveSearchTerm(validatedSuggestion)
    await performSearch(validatedSuggestion)
  } catch (error) {
    console.error('[handleSuggestionClick] Search failed:', error)
  } finally {
    skipNextSuggestionFetch.value = false
  }
}

// Handle keyboard navigation for suggestions and results
const handleSuggestionKeydown = (event: KeyboardEvent) => {
  const hasSuggestions = querySuggestions.value.length > 0
  const hasResults = navigableResults.value.length > 0

  if (event.key === 'Escape') {
    if (hasSuggestions) {
      querySuggestions.value = []
      selectedSuggestionIndex.value = -1
    }
    return
  }

  // Handle suggestions navigation when suggestions are visible
  if (hasSuggestions) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      selectedSuggestionIndex.value = Math.min(selectedSuggestionIndex.value + 1, querySuggestions.value.length - 1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      selectedSuggestionIndex.value = Math.max(selectedSuggestionIndex.value - 1, -1)
    } else if (event.key === 'Enter' && selectedSuggestionIndex.value >= 0) {
      event.preventDefault()
      handleSuggestionClick(querySuggestions.value[selectedSuggestionIndex.value])
    }
    return
  }

  // Handle results navigation when no suggestions but has results
  if (hasResults) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      selectedResultIndex.value = Math.min(selectedResultIndex.value + 1, navigableResults.value.length - 1)
      scrollSelectedResultIntoView()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      selectedResultIndex.value = Math.max(selectedResultIndex.value - 1, -1)
      scrollSelectedResultIntoView()
    } else if (event.key === 'Enter' && selectedResultIndex.value >= 0) {
      event.preventDefault()
      const selected = navigableResults.value[selectedResultIndex.value]
      if (selected) {
        handleResultClick(selected.item, selected.type)
      }
    }
  }
}

// Format canvas type for display
const formatCanvasType = (contentType: string | undefined): string => {
  if (!contentType) return t('canvasUI.CAlgoliaSearch.canvasFilters.saved')

  switch (contentType) {
    case 'saved_canvas':
      return t('canvasUI.CAlgoliaSearch.canvasFilters.saved')
    case 'template':
      return t('canvasUI.CAlgoliaSearch.canvasFilters.templates')
    case 'product_template':
      return t('canvasUI.CAlgoliaSearch.canvasFilters.productTemplates')
    case 'section':
      return t('canvasUI.CAlgoliaSearch.canvasFilters.products')
    case 'block':
      return t('canvasUI.CAlgoliaSearch.canvasFilters.blocks')
    default:
      return t('canvasUI.CAlgoliaSearch.canvasFilters.saved')
  }
}

// Extract and format snippet from Algolia result
const getSnippet = (item: any): string => {
  if (!searchQuery.value || searchQuery.value.trim() === '/') {
    return ''
  }

  const highlightValue = item._highlightResult?.searchable_content?.value
  if (!highlightValue) return ''

  const nameMatched = item._highlightResult?.name?.matchLevel && item._highlightResult.name.matchLevel !== 'none'

  if (nameMatched) {
    return ''
  }

  const emIndex = highlightValue.indexOf('<em>')
  if (emIndex === -1) return ''

  const startChar = Math.max(0, emIndex - 75)
  const endChar = Math.min(highlightValue.length, emIndex + 125)

  let truncated = highlightValue.substring(startChar, endChar)

  if (startChar > 0) truncated = '...' + truncated
  if (endChar < highlightValue.length) truncated = truncated + '...'

  return sanitizeHtml(truncated)
}

// File type toggle handler for checkboxes
const handleFileTypeToggle = (value: string, checked: boolean) => {
  if (checked) {
    if (!selectedFileTypes.value.includes(value)) {
      selectedFileTypes.value.push(value)
    }
  } else {
    selectedFileTypes.value = selectedFileTypes.value.filter((v) => v !== value)
  }
  debouncedFilterSearch()
}

// Canvas type toggle handler for checkboxes
const handleCanvasTypeToggle = (value: string, checked: boolean) => {
  if (checked) {
    if (!selectedCanvasFilters.value.includes(value)) {
      selectedCanvasFilters.value.push(value)
    }
  } else {
    selectedCanvasFilters.value = selectedCanvasFilters.value.filter((v) => v !== value)
  }
  debouncedFilterSearch()
}

// Clear canvas filter
const clearCanvasFilters = () => {
  selectedCanvasFilters.value = []
  showCanvasTypeDropdown.value = false
  debouncedFilterSearch()
}

// Clear filter helpers
const clearFileTypes = () => {
  selectedFileTypes.value = []
  showFileTypeDropdown.value = false
  debouncedFilterSearch()
}

// Track Algolia search events
function trackAlgoliaSearchPerformed(query: string, resultsCount: number) {
  try {
    track({
      event_name: CDP_EVENT_TYPE.algoliaSearchPerformed,
      payload: {
        query,
        results_count: resultsCount,
        search_type: searchType.value || 'global',
        instance_id: props.instanceId,
        is_admin: props.isAdmin === true,
      },
    }).catch(() => {})
  } catch (error) {
    console.warn('Failed to track Algolia search event:', error)
  }
}

function trackAlgoliaSearchNoResults(query: string) {
  try {
    track({
      event_name: CDP_EVENT_TYPE.algoliaSearchNoResults,
      payload: {
        query,
        search_type: searchType.value || 'global',
        instance_id: props.instanceId,
        is_admin: props.isAdmin === true,
      },
    }).catch(() => {})
  } catch (error) {
    console.warn('Failed to track no results event:', error)
  }
}

function trackAlgoliaResultClicked(item: any, type: string) {
  try {
    track({
      event_name: CDP_EVENT_TYPE.algoliaSearchResultClicked,
      payload: {
        result_type: type,
        result_id: item.id || item.file_id || item.canvas_id,
        result_name: item.name,
        search_query: searchQuery.value,
        instance_id: props.instanceId,
        is_admin: props.isAdmin === true,
      },
    }).catch(() => {})
  } catch (error) {
    console.warn('Failed to track result click event:', error)
  }
}

interface AlgoliaSearchResponse {
  content?: { hits: any[]; nbHits: number }
  canvas?: { hits: any[]; nbHits: number }
}

// Backend proxy endpoint for Algolia search
async function searchViaBackend(
  query: string,
  filters?: { content?: string; canvas?: string },
): Promise<AlgoliaSearchResponse> {
  if (!coreRestApiAxios?.value) {
    throw new Error('AUTH_REQUIRED')
  }

  try {
    const data = await coreRestApiAxios.value.post<unknown, AlgoliaSearchResponse>('protected/algolia/search', {
      query,
      instanceId: props.instanceId,
      filters,
      isAdminApp: props.isAdmin === true,
    })

    return data
  } catch (error: any) {
    if (error.response?.status === 401) {
      throw new Error('AUTH_EXPIRED')
    }

    if (error.response?.status === 403) {
      throw new Error('AUTH_FORBIDDEN')
    }

    console.error('Search failed:', error.response?.data || error.message)
    throw new Error('SEARCH_FAILED')
  }
}

function handleResultClick(item: any, type: 'canvas' | 'file' | 'folder') {
  saveRecentlyOpenedDoc(item, type)

  // Track result click
  trackAlgoliaResultClicked(item, type)

  emit('resultClick', item, type)
  emit('toggleSearch', false)
}

// Handle recently opened document click. also validate accessibility
async function handleRecentDocClick(item: any, type: 'canvas' | 'file' | 'folder') {
  try {
    if (!coreRestApiAxios?.value) {
      message.error(t('canvasUI.CAlgoliaSearch.errors.verificationFailed'))
      return
    }

    // Validate accessibility
    if (type === 'file') {
      // Files: Use ID-based validation via thumbnailsOnly mode
      const result = await coreRestApiAxios.value.post<unknown, { thumbnails: Record<string, string> }>(
        'protected/algolia/search',
        {
          thumbnailsOnly: true,
          fileIds: [item.id],
          instanceId: props.instanceId,
          isAdminApp: props.isAdmin === true,
        },
      )

      if (!result?.thumbnails?.[item.id]) {
        message.warning(t('canvasUI.CAlgoliaSearch.errors.contentDeleted'))
        return
      }
    } else {
      // Folders and canvases
      const results = await coreRestApiAxios.value.post<unknown, AlgoliaSearchResponse>('protected/algolia/search', {
        query: item.name,
        instanceId: props.instanceId,
        isAdminApp: props.isAdmin === true,
      })

      const hits = type === 'canvas' ? results?.canvas?.hits || [] : results?.content?.hits || []
      const itemExists = hits.some((hit: any) => {
        const hitId = hit.id || hit.file_id || hit.folder_id || hit.canvas_id
        return hitId === item.id
      })

      if (!itemExists) {
        message.warning(t('canvasUI.CAlgoliaSearch.errors.contentDeleted'))
        return
      }
    }

    saveRecentlyOpenedDoc(item, type)
    emit('resultClick', item, type)
    emit('toggleSearch', false)
  } catch (error) {
    console.error('Error validating file accessibility:', error)
    message.error(t('canvasUI.CAlgoliaSearch.errors.verificationFailed'))
  }
}

// Handle click outside suggestions dropdown
const handleClickOutside = (event: MouseEvent) => {
  if (querySuggestions.value.length === 0) return

  const target = event.target as Node
  const suggestionsEl = suggestionsDropdownRef.value
  const searchInputEl = searchInputRef.value

  if (suggestionsEl && !suggestionsEl.contains(target) && searchInputEl && !searchInputEl.contains(target)) {
    querySuggestions.value = []
    selectedSuggestionIndex.value = -1
  }
}

// Event listener cleanup
let searchInputListener: (() => void) | null = null
let setupRetryTimeout: NodeJS.Timeout | null = null

onMounted(() => {
  loadRecentSearches()
  loadRecentlyOpenedDocs()

  // Add click outside listener for suggestions dropdown
  document.addEventListener('click', handleClickOutside)

  nextTick(() => {
    if (searchInputRef.value) {
      searchInputRef.value.focus()
    }
  })
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)

  if (searchInputListener) {
    searchInputListener()
    searchInputListener = null
  }

  if (setupRetryTimeout) {
    clearTimeout(setupRetryTimeout)
    setupRetryTimeout = null
  }

  if (saveSearchTimer) {
    clearTimeout(saveSearchTimer)
    saveSearchTimer = null
  }

  if (searchTimer) {
    clearTimeout(searchTimer)
    searchTimer = null
  }

  if (filterChangeTimer) {
    clearTimeout(filterChangeTimer)
    filterChangeTimer = null
  }
})
</script>

<style scoped lang="scss">
:deep(.ais-SearchBox) {
  margin: 0;
}

:deep(.ais-SearchBox-submit),
:deep(.ais-SearchBox-reset) {
  display: none;
}

.admin-search :deep(.ais-SearchBox-reset) {
  display: block !important;
  position: absolute !important;
  right: 12px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  background: none !important;
  border: none !important;
  cursor: pointer !important;
  padding: 4px !important;
  color: var(--p-text3) !important;
  font-size: 16px !important;
  z-index: 10 !important;

  &:hover {
    color: var(--p-text2) !important;
  }

  &:focus {
    outline: none !important;
    color: var(--p-primary) !important;
  }
}

.admin-search :deep(.ais-SearchBox-input) {
  padding-right: 40px !important;
}

:deep(.ais-SearchBox-input) {
  width: 100%;
  padding: 12px 16px 12px 40px;
  border: 1px solid var(--p-primary4);
  border-radius: 6px;
  font-size: 14px;
  background-color: var(--p-base);
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--p-primary);
    box-shadow: 0 0 0 3px var(--p-primary5);
  }

  &::placeholder {
    color: var(--p-text3);
    font-size: 14px;
  }
}

.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: var(--p-border2) var(--p-surface2);
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: var(--p-surface2);
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: var(--p-border2);
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: var(--p-text3);
}

button:hover {
  transition: all 0.2s ease;
}

button:focus {
  outline: none;
}

:deep(mark),
:deep(em) {
  font-weight: bold;
  font-style: normal;
}
</style>
